import React, { useState } from 'react';
import { Button, CircularProgress, Alert, Snackbar } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';
import { openRazorpayCheckout } from '../../services/razorpay';
import type { RazorpayPaymentResponse } from '../../types/razorpay.d';

interface RazorpayCheckoutProps {
  tierId: string;
  tierName: string;
  billingCycle: 'monthly' | 'annual';
  disabled?: boolean;
  /** Called after Firestore subscription is activated. */
  onSuccess?: () => void;
}

/**
 * Button that drives the full Razorpay payment flow:
 *   1. Create order on the backend
 *   2. Open Razorpay checkout modal (supports international Visa / Mastercard / Amex)
 *   3. Verify HMAC signature on the backend
 *   4. Navigate to /pricing?payment=success on confirmation
 *
 * For test mode use card 4111 1111 1111 1111, any future expiry, any CVV.
 */
export function RazorpayCheckout({
  tierId,
  tierName,
  billingCycle,
  disabled = false,
  onSuccess,
}: RazorpayCheckoutProps) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleClick = async () => {
    if (processing || disabled) return;
    setProcessing(true);
    setErrorMsg(null);

    try {
      // Step 1 – create order on backend
      const orderData = await apiClient.createRazorpayOrder(tierId, billingCycle);

      // Step 2 – open Razorpay modal
      openRazorpayCheckout({
        orderData,
        tierName,
        prefill: {
          name: currentUser?.displayName ?? '',
          email: currentUser?.email ?? '',
        },
        onSuccess: async (response: RazorpayPaymentResponse) => {
          try {
            // Step 3 – verify signature and activate subscription
            await apiClient.verifyRazorpayPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              tierId,
              billingCycle,
            });

            onSuccess?.();
            navigate('/pricing?payment=success&provider=razorpay');
          } catch (verifyErr: any) {
            setErrorMsg(
              verifyErr?.response?.data?.message ||
              verifyErr?.message ||
              'Payment received but verification failed. Contact support.'
            );
          } finally {
            setProcessing(false);
          }
        },
        onFailure: (msg: string) => {
          // User dismissed or card was declined – not necessarily an error worth showing
          if (msg !== 'Payment cancelled.') {
            setErrorMsg(msg);
          }
          setProcessing(false);
        },
      });
    } catch (err: any) {
      setErrorMsg(
        err?.response?.data?.message || err?.message || 'Failed to initiate payment.'
      );
      setProcessing(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        size="large"
        startIcon={processing ? <CircularProgress size={18} /> : <CreditCardIcon />}
        onClick={handleClick}
        disabled={processing || disabled}
      >
        {processing ? 'Processing…' : 'Pay with Card'}
      </Button>

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={6000}
        onClose={() => setErrorMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ width: '100%' }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </>
  );
}
